import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// Unified per-user invoices (CLE-238). Mirrors the backend
// modules/userInvoices service: every invoice associated with a user across all
// sources (SmartBill, manual upload, email ingest) with a financial summary.

export type InvoiceFlow = 'income' | 'outcome';
export type InvoiceSource = 'smartbill' | 'manual' | 'email';

export interface UnifiedInvoice {
  id: string;
  source: InvoiceSource;
  invoiceNumber: string;
  flow: InvoiceFlow;
  description: string | null;
  category: string | null;
  status: string;
  currency: string;
  net: number;
  vat: number;
  total: number;
  issueDate: string | null;
  dueDate: string | null;
  paidAt: string | null;
  pdfUrl: string | null;
  fileName: string | null;
}

export interface UserInvoiceSummary {
  totalPaid: number;
  totalPending: number;
  totalIncome: number;
  totalOutcome: number;
  count: number;
  byCategory: Array<{ category: string; income: number; outcome: number; count: number }>;
}

export interface UserInvoicesResponse {
  invoices: UnifiedInvoice[];
  summary: UserInvoiceSummary;
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface UserInvoiceFilters {
  dateFrom?: string;
  dateTo?: string;
  type?: InvoiceFlow;
  status?: string;
  category?: string;
  page?: number;
  limit?: number;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildQuery(filters: UserInvoiceFilters): string {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.type) params.set('type', filters.type);
  if (filters.status) params.set('status', filters.status);
  if (filters.category) params.set('category', filters.category);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// GET /api/users/:userId/invoices
export async function listUserInvoices(
  userId: string,
  filters: UserInvoiceFilters = {},
): Promise<UserInvoicesResponse> {
  const res = await apiFetch<Envelope<UserInvoicesResponse>>(
    `/api/users/${userId}/invoices${buildQuery(filters)}`,
    { headers: authHeaders() },
  );
  return res.data;
}
